import React, { useEffect, useState } from 'react';
import { styled } from '@this/constants/themes';
import { observer } from 'mobx-react';
import axios from 'axios';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import useDebounce from '../../../../lib/hooks/useDebounce';

interface Props {
  value: string;
  helperText: string;
  label: string;
  placeholder: string;
  disabled?: boolean;
  onChange: (value: string | Suggest | null, placeId?: string) => void;
  onInputChange: (value: string) => void;
}

interface Suggest {
  id: number;
  mainText: string;
  subText: string;
  img: string;
  kind: string;
  placeId?: string;
}

interface SuggestsResponse {
  suggests: SuggestsResponseItem[];
}

interface SuggestsResponseItem {
  id: number;
  main_text: string;
  secondary_text: string;
  type: 'history' | 'place' | 'city' | 'airport' | 'station' | 'base';
  place_id?: string;
}

const PlaceInput = observer(
  ({ value, helperText, label, placeholder, disabled, onChange, onInputChange }: Props) => {
    const [suggests, setSuggests] = useState<Suggest[]>([]);
    const debouncedValue = useDebounce(value, 200);

    const fetchSuggeests = async () => {
      const params = {
        query: debouncedValue,
        kind: 'calendar'
      };
      const res = await axios.get<SuggestsResponse>('/suggests.json', { params });
      const orderObj = { base: 1, history: 2, city: 3, airport: 4, station: 5, place: 6 };
      const newSuggests = res.data.suggests
        .sort((s1, s2) => orderObj[s1.type] - orderObj[s2.type])
        .map(s => ({
          id: s.id,
          mainText: s.main_text,
          subText: s.secondary_text,
          img: `${s.type}.png`,
          kind: s.type,
          placeId: s.place_id
        }));
      setSuggests(newSuggests);
    };

    useEffect(() => {
      fetchSuggeests();
    }, [debouncedValue]);

    return (
      <Autocomplete
        options={suggests}
        getOptionLabel={option => option.mainText || ''}
        renderOption={option => (
          <Option>
            <OptionIcon src={`/images/${option.img}`} width="15" height="18" alt="place" />
            <div>
              <MainText>{option.mainText}</MainText>
              <SubText>{option.subText}</SubText>
            </div>
          </Option>
        )}
        filterOptions={options => options}
        inputValue={value}
        onInputChange={(_, val, reason) => {
          if (['input', 'clear'].includes(reason) || (reason === 'reset' && val.length !== 0)) onInputChange(val);
        }}
        value={helperText}
        disabled={disabled || false}
        onChange={(_, val) => onChange(val, typeof val === 'object' && val !== null ? val.placeId : undefined)}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={placeholder}
            label={label}
            helperText={helperText}
            InputLabelProps={{ shrink: true }}
          />
        )}
        freeSolo
        style={{ flexGrow: 999 }}
      />
    );
  }
);

const Option = styled.div`
  display: flex;
  align-items: center;
`;

const OptionIcon = styled.img`
  display: block;
  margin-right: 10px;
`;

const MainText = styled.div`
  font-size: 0.9rem;
  white-space: nowrap;
`;

const SubText = styled.div`
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.54);
`;

export default PlaceInput;
